import { useState, useEffect, useMemo } from "react";
import toast from "../../utils/toast";
import { useParams, useHistory } from "react-router-dom";
import { adsApi, uploadApi } from "../../services";
import "./adsForm.css";

export default function AdsForm() {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    link: "",
    image: "",
    position: "bottom",
    isHide: true
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);

  // Memoize the blob preview URL so it isn't recreated on every render
  // (otherwise the <img> re-fetches continuously and leaks object URLs).
  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => { if (filePreview) URL.revokeObjectURL(filePreview); };
  }, [filePreview]);

  const PF = "https://backend.hoanglecf.com/images/";
  // const PF = "http://localhost:5000/images/";

  useEffect(() => {
    if (isEdit) {
      fetchAdData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchAdData = async () => {
    try {
      setLoadingData(true);
      const ad = await adsApi.getAdById(id);
      setFormData({
        title: ad.title || "",
        link: ad.link || "",
        image: ad.image || "",
        position: ad.position || "bottom",
        isHide: ad.isHide !== undefined ? ad.isHide : true
      });
    } catch (error) {
      console.error("Error fetching ad:", error);
      toast.error("Không thể tải dữ liệu quảng cáo!");
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.warning("Vui lòng nhập tiêu đề quảng cáo!");
      return;
    }
    
    if (!formData.link.trim()) {
      toast.warning("Vui lòng nhập link quảng cáo!");
      return;
    }

    if (!file && !formData.image) {
      toast.warning("Vui lòng chọn ảnh quảng cáo!");
      return;
    }

    try {
      setLoading(true);
      
      let imageUrl = formData.image;
      
      // Upload new image if file is selected
      if (file) {
        const tempName = Date.now() + "-" + file.name;
        const res = await uploadApi.uploadFile(file, tempName);
        imageUrl = `/${res?.filename || tempName}`;
      }

      const adData = {
        ...formData,
        image: imageUrl
      };

      if (isEdit) {
        await adsApi.updateAd(id, adData);
        toast.success("Cập nhật quảng cáo thành công!");
      } else {
        await adsApi.createAd(adData);
        toast.success("Tạo quảng cáo thành công!");
      }
      
      history.push("/ads");
    } catch (error) {
      console.error("Error saving ad:", error);
      toast.error("Có lỗi xảy ra khi lưu quảng cáo!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/ads");
  };

  if (loadingData) {
    return (
      <div className="ads-form-page">
        <div className="ads-form-container">
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Đang tải...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-form-page">
      <div className="ads-form-container">
        <div className="ads-form-header">
          <h1>{isEdit ? "Chỉnh sửa quảng cáo" : "Tạo quảng cáo mới"}</h1>
          <p>{isEdit ? "Cập nhật thông tin quảng cáo" : "Điền thông tin để tạo quảng cáo mới"}</p>
        </div>

        <form className="ads-form" onSubmit={handleSubmit}>
          {/* Image Upload */}
          <div className="form-section">
            <label className="form-label">Ảnh quảng cáo <span className="required">*</span></label>
            <div className="image-upload-area">
              {file ? (
                <div className="image-preview">
                  <img src={filePreview} alt="Preview" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={() => setFile(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : formData.image ? (
                <div className="image-preview">
                  <img src={PF +formData.image} alt="Current" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={() => setFormData(prev => ({ ...prev, image: "" }))}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <label htmlFor="fileInput" className="upload-button">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Chọn ảnh</span>
                  <small>JPG, PNG, GIF (tối đa 5MB)</small>
                </label>
              )}
              <input
                id="fileInput"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept="image/*"
              />
            </div>
          </div>

          {/* Title */}
          <div className="form-section">
            <label className="form-label">Tiêu đề quảng cáo <span className="required">*</span></label>
            <div className="input-group">
              <i className="fas fa-heading input-icon"></i>
              <input
                className="form-input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Nhập tiêu đề quảng cáo"
                required
              />
            </div>
          </div>

          {/* Link */}
          <div className="form-section">
            <label className="form-label">Link quảng cáo <span className="required">*</span></label>
            <div className="input-group">
              <i className="fas fa-link input-icon"></i>
              <input
                className="form-input"
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="https://example.com"
                required
              />
            </div>
          </div>

          {/* Position */}
          <div className="form-section">
            <label className="form-label">Vị trí hiển thị <span className="required">*</span></label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="position"
                  value="bottom"
                  checked={formData.position === "bottom"}
                  onChange={handleInputChange}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Bottom (Cuối trang)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="position"
                  value="right1"
                  checked={formData.position === "right1"}
                  onChange={handleInputChange}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Right 1 (Bên phải 1)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="position"
                  value="right2"
                  checked={formData.position === "right2"}
                  onChange={handleInputChange}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Right 2 (Bên phải 2)</span>
              </label>
            </div>
          </div>

          {/* Visibility */}
          <div className="form-section">
            <label className="form-label">Trạng thái hiển thị</label>
            <div className="checkbox-group">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="isHide"
                  checked={formData.isHide}
                  onChange={handleInputChange}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Ẩn quảng cáo (không hiển thị trên website)</span>
              </label>
            </div>
            <small className="form-help">
              Bỏ chọn để hiển thị quảng cáo trên website
            </small>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              <i className="fas fa-times"></i>
              <span>Hủy</span>
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  <span>{isEdit ? "Cập nhật" : "Tạo quảng cáo"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
